import { v1Connector } from "./formats/v1";
import { v2Connector } from "./formats/v2";
import type {
  JsonFormatConnector,
  LoadedData,
  ParseJsonOptions,
} from "./formats/shared";
import { migrateV1DocumentToV2 } from "./migrations/v1ToV2";

/**
 * Point d'entrée public, versionné (voir BESOINS.md §8.1) : lit `configurations.formatVersion`
 * (absent = `1`, le format historique — voir `formats/v1.ts`) et délègue au connecteur
 * correspondant (voir `formats/shared.ts`'s `JsonFormatConnector`). Rien en aval (`createTokaMap`,
 * `ShapeStore`, etc.) n'a besoin de savoir combien de versions existent : ces fonctions restent
 * l'unique point d'entrée, quelle que soit la version réellement rencontrée.
 */

export type {
  LoadedData,
  LoadedView,
  ParseJsonFeatureOptions,
  ParseJsonOptions,
} from "./formats/shared";
export {
  lngLatToMercator,
  mercatorToLngLat,
  parseProjectThemes,
  rawRotationToDeg,
  rotationDegToRawRotation,
  withProjectThemes,
} from "./formats/shared";
export type {
  RawFeature,
  RawFeatureProperties,
} from "./formats/v1";
export { parseJsonFeature, shapeToRawFeature } from "./formats/v1";
export type {
  RawFeatureV2,
  RawFeaturePropertiesV2,
} from "./formats/v2";
export { parseJsonFeatureV2, shapeToRawFeatureV2 } from "./formats/v2";

/**
 * Alias version-agnostiques vers le connecteur le plus récent (actuellement v2) — pour un appelant
 * qui construit/consomme un feature isolé sans jamais vouloir épingler une version explicite (ex.
 * `createTokaMap`'s `addShapeFromFeature`). Un `Shape` seul ne porte pas de `formatVersion` (voir
 * `RawConfigurations.formatVersion`, qui n'existe qu'au niveau document), donc pas de dispatch à
 * l'exécution possible ici comme pour `parseJsonDocument` — juste ce point de réexport unique à mettre à
 * jour (`./formats/v2` -> `./formats/v3`, etc.) le jour où `LATEST_FORMAT_VERSION` change à nouveau.
 */
export type { RawFeatureV2 as LatestRawFeature } from "./formats/v2";
export {
  parseJsonFeatureV2 as parseLatestJsonFeature,
  shapeToRawFeatureV2 as shapeToLatestRawFeature,
} from "./formats/v2";

const FORMAT_CONNECTORS = new Map<number, JsonFormatConnector>(
  [v1Connector, v2Connector].map((connector) => [connector.version, connector]),
);

/** Dernière version de format connue de cette build (voir BESOINS.md §8.1) — sert de cible à
 * `migrateDocumentToLatest` et de référence à `createTokaMap`'s passerelle de migration
 * (`TokaMapConfig.onMigrateDocument`) pour savoir si un document chargé est à jour. */
export const LATEST_FORMAT_VERSION = Math.max(...FORMAT_CONNECTORS.keys());

function resolveFormatConnector(formatVersion: number): JsonFormatConnector {
  const connector = FORMAT_CONNECTORS.get(formatVersion);
  if (!connector) {
    throw new Error(
      `[jsonLoader] formatVersion ${formatVersion} non supporté (versions connues : ` +
        `${[...FORMAT_CONNECTORS.keys()].join(", ")}) — voir BESOINS.md §8.1.`,
    );
  }
  return connector;
}

/**
 * Un palier de migration par version SOURCE (pas par paire de versions) : `MIGRATIONS[N]` migre un
 * document de la version `N` vers `N + 1` (jamais directement vers `LATEST_FORMAT_VERSION`) —
 * `migrateDocumentToLatest` ci-dessous enchaîne les paliers nécessaires. Ajouter une v3 ne demande
 * donc JAMAIS de fonction `v1ToV3` : seule `MIGRATIONS[2] = migrateV2DocumentToV3` s'ajoute, et
 * `MIGRATIONS[1]` (`migrateV1DocumentToV2`, déjà là) est réutilisée telle quelle pour le premier
 * palier d'un document v1 — la boucle fait le reste.
 */
const MIGRATIONS: Record<number, (raw: unknown) => unknown> = {
  1: migrateV1DocumentToV2,
};

/**
 * Transforme un document brut (n'importe quelle version connue) en document brut au format le
 * plus récent (`LATEST_FORMAT_VERSION`) — utilisé par `createTokaMap`'s passerelle de migration
 * pour construire le paramètre de `TokaMapConfig.onMigrateDocument` (voir BESOINS.md §8.1). No-op
 * si le document est déjà à jour. Enchaîne les paliers de `MIGRATIONS` un par un (voir son
 * commentaire) plutôt que de supposer un unique saut direct vers `LATEST_FORMAT_VERSION` — reste
 * donc correct même après l'ajout d'une v3 (ou plus), sans changement ici.
 */
export function migrateDocumentToLatest(raw: unknown): unknown {
  let doc = raw;
  let formatVersion = readFormatVersion(doc) ?? 1;
  while (formatVersion !== LATEST_FORMAT_VERSION) {
    const migrate = MIGRATIONS[formatVersion];
    if (!migrate) {
      throw new Error(
        `[jsonLoader] pas de chemin de migration connu depuis formatVersion ${formatVersion} ` +
          `vers ${LATEST_FORMAT_VERSION}.`,
      );
    }
    doc = migrate(doc);
    formatVersion = readFormatVersion(doc) ?? formatVersion + 1;
  }
  return doc;
}

/** Lit `configurations.formatVersion` sur un document brut (n'importe quelle version) — voir
 * `formats/shared.ts`'s `RawConfigurations.formatVersion`. */
function readFormatVersion(doc: unknown): number | undefined {
  return (doc as { configurations?: { formatVersion?: number } } | null)?.configurations?.formatVersion;
}

/**
 * Le fichier a été vu sous deux formes : un JSON simple, ou un JSON "doublement encodé"
 * (une chaîne JSON contenant le vrai document — probablement un artefact d'export). On gère
 * les deux sans présumer laquelle sera fournie.
 */
function parseJsonMaybeDoubleEncoded(rawText: string): unknown {
  const first = JSON.parse(rawText);
  return typeof first === "string" ? JSON.parse(first) : first;
}

/**
 * Fonction pure (pas d'I/O) : reprojette un document déjà parsé (voir BESOINS.md §8 pour le
 * format). Distincte de `parseJson` (qui part d'un texte brut) pour les cas où le JSON est
 * déjà disponible sous forme d'objet — ex. `TokaMapConfig.loadJson` dans `createTokaMap`, qui
 * ne passe pas par le texte brut et n'a donc pas à re-sérialiser/re-parser pour réutiliser cette
 * logique.
 */
export function parseJsonDocument(data: unknown, options: ParseJsonOptions = {}): LoadedData {
  const formatVersion = readFormatVersion(data) ?? 1;
  return { ...resolveFormatConnector(formatVersion).parseDocument(data, options), formatVersion };
}

/** Fonction pure (pas d'I/O) : parse un texte brut + reprojette. Voir BESOINS.md §8 pour le format. */
export function parseJson(rawText: string, options: ParseJsonOptions = {}): LoadedData {
  return parseJsonDocument(parseJsonMaybeDoubleEncoded(rawText), options);
}

/** Charge et parse le fichier à l'URL donnée. Tolérant : ne lève pas si le fichier est absent/invalide. */
export async function loadShapesFromUrl(url: string, options: ParseJsonOptions = {}): Promise<LoadedData> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error(`[jsonLoader] échec du chargement de ${url}: HTTP ${response.status}`);
      return { shapes: [] };
    }
    const text = await response.text();
    return parseJson(text, options);
  } catch (error) {
    console.error(`[jsonLoader] échec du chargement de ${url}:`, error);
    return { shapes: [] };
  }
}
